import {
  ComponentType,
  createContext,
  MutableRefObject,
  ReactNode,
  RefObject,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const swipeThreshold = 75;
// Needed so user can still scroll vertically on page when touching the carousel
const preventPropagationAt = 10;

type ICarouselContext = {
  activeStepRef: RefObject<HTMLDivElement>;
  containerRef: RefObject<HTMLDivElement>;
  goToStep: (stepIndex: number) => void;
  goToNext: () => void;
  goToPrevious: () => void;
  infinite: boolean | undefined;
  startSwipe: (e: SyntheticEvent) => void;
  moveSwipe: (e: SyntheticEvent) => void;
  endSwipe: (e: SyntheticEvent) => void;
  stepIndex: number;
  steps: any[];
};

export const CarouselContext = createContext<ICarouselContext>({} as ICarouselContext);

type CarouselOptions = {
  // Duration in milliseconds
  autoPlayDuration?: number;
  // If carousel should loop infinitely - hitting next on last step will go back to first, and vice versa
  infinite?: boolean;
  steps: any[];
};

type CarouselProviderProps = CarouselOptions & {
  children: ReactNode;
};

export const CarouselProvider: React.FC<CarouselProviderProps> = ({
  children,
  steps: items = [],
  infinite,
  autoPlayDuration,
}: CarouselProviderProps) => {
  const [stepIndex, setStepIndex] = useState(0);
  const startPosRef = useRef(0);
  const endPosRef = useRef(0);
  const activeStepRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>(null) as MutableRefObject<NodeJS.Timeout>;
  const steps = useMemo(() => items || [], [items]);

  const resetStepPos = () => {
    if (activeStepRef.current) {
      activeStepRef.current.style.transform = "";
    }
  };

  const goToPrevious = useCallback(() => {
    if (stepIndex !== 0) {
      setStepIndex(prevIndex => prevIndex - 1);
    } else if (infinite) {
      // Go back to last item
      setStepIndex(steps.length - 1);
    } else {
      resetStepPos();
    }
  }, [stepIndex, infinite, steps.length]);

  const goToNext = useCallback(() => {
    if (stepIndex !== steps.length - 1) {
      setStepIndex(prevIndex => prevIndex + 1);
    } else if (infinite) {
      // Go back to first item
      setStepIndex(0);
    } else {
      resetStepPos();
    }
  }, [stepIndex, infinite, steps.length]);

  const goToStep = useCallback(index => {
    setStepIndex(index);
  }, []);

  const startSwipe = useCallback(e => {
    if (!e.touches?.[0]) {
      return;
    }

    startPosRef.current = e.touches[0].clientX;
    endPosRef.current = e.touches[0].clientX;
  }, []);

  const moveSwipe = useCallback(
    e => {
      if (!e.touches?.[0]) {
        return;
      }

      // Stop user from trying to scroll left on first step, or right on last
      if (
        !infinite &&
        ((stepIndex === 0 && startPosRef.current < endPosRef.current) ||
          (stepIndex === steps.length - 1 && startPosRef.current > endPosRef.current))
      ) {
        return;
      }

      const delta = Math.abs(startPosRef.current - endPosRef.current);
      endPosRef.current = e.touches[0].clientX;

      if (activeStepRef.current && delta > preventPropagationAt) {
        activeStepRef.current.style.transform = `translateX(${-(startPosRef.current - endPosRef.current)}px)`;
      }
    },
    [stepIndex, infinite, steps]
  );

  const endSwipe = useCallback(() => {
    const delta = Math.abs(startPosRef.current - endPosRef.current);

    // Swipe after min threshold is met
    if (delta > swipeThreshold) {
      if (startPosRef.current > endPosRef.current) {
        // Swiping to the right
        goToNext();
      } else {
        // Swiping to the left
        goToPrevious();
      }
    } else {
      resetStepPos();
    }

    startPosRef.current = 0;
    endPosRef.current = 0;
  }, [goToNext, goToPrevious]);

  // Prevent container propagating touch event to window when dragging horizontally
  const preventTouch = useCallback(e => {
    if (e.cancelable && Math.abs(startPosRef.current - endPosRef.current) > preventPropagationAt) {
      e.preventDefault();
    }
  }, []);

  const value = useMemo(
    () => ({
      containerRef,
      endSwipe,
      moveSwipe,
      startSwipe,
      goToStep,
      goToNext,
      goToPrevious,
      stepIndex,
      steps,
      activeStepRef,
      infinite,
    }),
    [endSwipe, moveSwipe, startSwipe, goToStep, goToNext, goToPrevious, stepIndex, steps, infinite]
  );

  useEffect(() => {
    resetStepPos();

    if (autoPlayDuration) {
      timeoutRef.current = setTimeout(() => {
        goToNext();
      }, autoPlayDuration);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [stepIndex, goToNext, autoPlayDuration]);

  useEffect(() => {
    const ref = containerRef.current;

    if (ref) {
      ref.addEventListener("touchmove", preventTouch, { passive: false });
    }

    return () => {
      if (ref) {
        ref.addEventListener("touchmove", preventTouch, { passive: false });
      }
    };
  }, [preventTouch]);

  if (!steps?.length) {
    return null;
  }

  return <CarouselContext.Provider value={value}>{children}</CarouselContext.Provider>;
};

export const useCarousel = () => useContext(CarouselContext);

export default useCarousel;

// Wrap a component in the Carousel Provider
export const withCarousel = <P extends object>(Component: ComponentType<P>, options: CarouselOptions): React.FC<P> => {
  const CarouselWrapper = (props: P) => (
    <CarouselProvider {...options}>
      <Component {...props} />
    </CarouselProvider>
  );
  return CarouselWrapper;
};
